import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { syncronizeAllDataAction } from '@symphony-solo/domain/sync-data/src/';
import { ToastFacade } from '@symphony-solo/domain/toast/src';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ErrorCode } from '@symphony-solo/shared/error-handling/src';
import { LOCATION_SELECTION_TABS } from '@symphony-solo/shared/utils/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LocationService } from '../services';
import {
  authenticateLocationAction,
  authenticateLocationErrorAction,
  authenticateLocationSuccessAction,
  locationBarcodeScannedAction,
  reAuthenticateLocationAction,
} from './location.actions';
import { LocationFacade } from './location.facade';

@Injectable()
export class LocationEffects {
  constructor(
    private action$: Actions,
    private locationService: LocationService,
    private toastFacade: ToastFacade,
    private locationFacade: LocationFacade,
    private navController: NavController
  ) {}

  protected authenticateLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(authenticateLocationAction),
      switchMap((payload) => {
        return this.locationService.authenticateLocation(payload.locationGuid).pipe(
          map((locationResponse) => authenticateLocationSuccessAction({ locationResponse, locationGuid: payload.locationGuid })),
          catchError(() => {
            const errorMessage = 'Incorrect location code';
            this.toastFacade.showToastErrorMessage(errorMessage);

            const failures = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMessage, ErrorCode.InvalidLocationBarcode))
              .build();

            return of(authenticateLocationErrorAction({ failures }));
          })
        );
      })
    )
  );

  protected authenticateLocationSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(authenticateLocationSuccessAction),
      switchMap(() => {
        return of(syncronizeAllDataAction());
      })
    )
  );

  protected authenticateLocationError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(authenticateLocationErrorAction),
        tap(() => {
          this.navController.navigateRoot(LOCATION_SELECTION_TABS);
        })
      ),
    { dispatch: false }
  );

  protected locationBarcodeScannedAction$ = createEffect(() =>
    this.action$.pipe(
      ofType(locationBarcodeScannedAction),
      switchMap((payload) => {
        return of(authenticateLocationAction({ locationGuid: payload.barcode }));
      })
    )
  );

  protected reAuthenticateLocation$ = createEffect(() =>
    this.action$.pipe(
      ofType(reAuthenticateLocationAction),
      withLatestFrom(this.locationFacade.locationGuid$),
      switchMap(([_, locationGuid]) => {
        return of(authenticateLocationAction({ locationGuid: locationGuid }));
      })
    )
  );
}
